/**
 * Safe classification and copy for the contract-detail read path.
 *
 * The server may include useful diagnostics in a tRPC error, but none of
 * those diagnostics are suitable for rendering. The UI only receives one of
 * the stable, user-facing categories below.
 */

export type ContractDetailErrorKind =
  | "FORBIDDEN"
  | "NOT_FOUND"
  | "INTERNAL_SERVER_ERROR";

export interface ClassifiedContractDetailError {
  kind: ContractDetailErrorKind;
  message: string;
  canRetry: boolean;
}

export const CONTRACT_DETAIL_ERROR_MESSAGES = {
  FORBIDDEN: "No tienes permiso para acceder a este contrato.",
  NOT_FOUND: "No se encontró el contrato.",
  INTERNAL_SERVER_ERROR: "No se pudo cargar el contrato. Inténtalo de nuevo.",
} as const;

type ErrorShape = {
  code?: unknown;
  httpStatus?: unknown;
};

type ContractDetailErrorLike = {
  data?: ErrorShape;
  shape?: { data?: ErrorShape };
  message?: unknown;
  status?: unknown;
};

function asStatus(value: unknown): number | undefined {
  return typeof value === "number" && Number.isInteger(value)
    ? value
    : undefined;
}

function asCode(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

export function classifyContractDetailError(
  error: unknown
): ClassifiedContractDetailError {
  const candidate = (error ?? {}) as ContractDetailErrorLike;
  const data = candidate.data ?? candidate.shape?.data;
  const code = asCode(data?.code);
  const status = asStatus(data?.httpStatus) ?? asStatus(candidate.status);
  const rawMessage =
    typeof candidate.message === "string" ? candidate.message : "";

  if (
    code === "FORBIDDEN" ||
    code === "UNAUTHORIZED" ||
    status === 401 ||
    status === 403
  ) {
    return {
      kind: "FORBIDDEN",
      message: CONTRACT_DETAIL_ERROR_MESSAGES.FORBIDDEN,
      canRetry: false,
    };
  }

  if (
    code === "NOT_FOUND" ||
    status === 404 ||
    rawMessage === "CONTRACT_NOT_FOUND"
  ) {
    return {
      kind: "NOT_FOUND",
      message: CONTRACT_DETAIL_ERROR_MESSAGES.NOT_FOUND,
      canRetry: false,
    };
  }

  // This is deliberately also the safe fallback for an unknown client-side
  // failure. It is the only category that offers a retry, and its message is
  // independent of server/DB/SQL diagnostics.
  return {
    kind: "INTERNAL_SERVER_ERROR",
    message: CONTRACT_DETAIL_ERROR_MESSAGES.INTERNAL_SERVER_ERROR,
    canRetry: true,
  };
}

export function getContractDetailReturnUrl(
  routeCustomerPartnerId: number | null | undefined
): string {
  if (
    typeof routeCustomerPartnerId === "number" &&
    Number.isSafeInteger(routeCustomerPartnerId) &&
    routeCustomerPartnerId > 0
  ) {
    return `/customers/${routeCustomerPartnerId}?tab=contratos`;
  }
  return "/customers";
}

/**
 * NAV-01 (P0-B): pure tab <-> query-string helpers for the contract detail
 * route. The visible tab is synchronized with `?tab=` so deep links, browser
 * back/forward and refresh keep the customer-360 context. Unrelated query
 * parameters are always preserved.
 */

export type ContractDetailTab =
  | "resumen"
  | "instalaciones"
  | "economia"
  | "certificaciones"
  | "documentos"
  | "partes";

export const DEFAULT_CONTRACT_DETAIL_TAB: ContractDetailTab = "resumen";

const CONTRACT_DETAIL_TAB_ALIASES: Record<string, ContractDetailTab> = {
  resumen: "resumen",
  instalaciones: "instalaciones",
  installations: "instalaciones",
  economia: "economia",
  economy: "economia",
  billing: "economia",
  certificaciones: "certificaciones",
  certifications: "certificaciones",
  documentos: "documentos",
  partes: "partes",
};

export function parseContractDetailTab(
  value: string | null | undefined
): ContractDetailTab {
  const normalized = (value ?? "").trim().toLowerCase();
  return CONTRACT_DETAIL_TAB_ALIASES[normalized] ?? DEFAULT_CONTRACT_DETAIL_TAB;
}

export function getContractDetailTabFromSearch(search: string): ContractDetailTab {
  const params = new URLSearchParams(search);
  return parseContractDetailTab(params.has("certificationId") ? "certificaciones" : params.get("tab"));
}

export function applyContractDetailTabToSearch(
  search: string,
  tab: ContractDetailTab
): string {
  const params = new URLSearchParams(search);
  if (tab === DEFAULT_CONTRACT_DETAIL_TAB) {
    params.delete("tab");
  } else {
    params.set("tab", tab);
  }
  const queryString = params.toString();
  return queryString ? `?${queryString}` : "";
}
